import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Monitor, LayoutGrid, Building2, LayoutDashboard, Bell, Calculator,
  ShoppingBag, AlertTriangle, CheckCheck, Trash2, ChevronLeft, MoreHorizontal, X,
  type LucideIcon,
} from 'lucide-react'
import { SidebarTrigger } from '../ui/sidebar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'
import { useIsMobile } from '../../hooks/use-mobile'
import { usePermissions } from '../../hooks/usePermissions'
import { useEntitlements } from '../../hooks/useEntitlements'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu'
import { cn } from '../../lib/utils'
import { VersionBadge } from '../VersionBadge'
import { usePageHeaderValue } from './PageHeader'
import { useNotifications, formatRelativeTime } from '../../contexts/NotificationContext'
import TeamButton from '../team/TeamPanel'
import { WetookMark } from '../WetookMark'
import { PocketMark } from '../PocketMark'

// ── App switcher config ──────────────────────────────────────────────────────
const APP_URLS = {
  signage: ((import.meta as any).env?.VITE_FRAME_URL as string || '').replace(/\/$/, ''),
  page:    ((import.meta as any).env?.VITE_PING_URL    as string || '').replace(/\/$/, ''),
  my:      ((import.meta as any).env?.VITE_MY_URL      as string || '').replace(/\/$/, ''),
  pos:     ((import.meta as any).env?.VITE_POS_URL     as string || '').replace(/\/$/, ''),
}
const CURRENT_ORIGIN = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : ''
const IS_POS_APP = APP_URLS.pos === '' || APP_URLS.pos === CURRENT_ORIGIN

type AppEntry = {
  key: 'signage' | 'page' | 'my' | 'pos'
  label: string
  description: string
  icon: LucideIcon
  href: string
  color: string
  ring: string
  // Optional brand image shown in the tile instead of the lucide `icon`.
  img?: string
  // Optional inline logo node (theme-adaptive, e.g. the WeTook mark) — wins
  // over `img`/`icon` and renders WITHOUT the coloured tile background.
  logo?: ReactNode
  // Hidden apps are dropped from the switcher (kept in config for easy re-enable).
  hidden?: boolean
}

const APPS: AppEntry[] = [
  {
    key: 'signage',
    label: 'Frame',
    description: 'Screens & content',
    icon: Monitor,
    img: '/frame-logo.avif',
    href: '/',
    color: 'bg-blue-500/10 text-blue-600',
    ring: 'ring-blue-500/30',
  },
  {
    key: 'page' as const,
    label: 'Ping',
    description: 'Brand page builder',
    icon: LayoutGrid,
    href: '/',
    color: 'bg-orange-500/10 text-orange-600',
    ring: 'ring-orange-500/30',
  },
  {
    key: 'pos' as const,
    label: 'Pocket',
    description: 'Till & invoices',
    icon: Calculator,
    // The brand mark wins over the generic icon where one exists — same
    // arrangement the My tile already uses.
    logo: <PocketMark className="w-6 h-6 text-foreground" />,
    // The tray lands on Cashier's dashboard, not the till. The register is a
    // full-bleed operator surface reached from there ("Open register"), so
    // switching apps drops you in the back office like every other tile.
    href: '/',
    color: 'bg-teal-500/10 text-teal-600',
    ring: 'ring-teal-500/30',
  },
  {
    key: 'my' as const,
    label: 'My',
    description: 'Org & management',
    icon: Building2,
    logo: <WetookMark className="w-6 h-6 text-foreground" />,
    href: '/',
    color: 'bg-emerald-500/10 text-emerald-600',
    ring: 'ring-emerald-500/30',
  },
]

// App-switcher key → the RBAC app_kind that gates it. 'my' isn't a product
// app (never gated) so it maps to null and always stays visible.
const APP_KIND_MAP: Record<AppEntry['key'], string | null> = {
  signage: 'signage',
  page: 'ping',
  pos: 'pos',
  my: null,
}

function AppSwitcher() {
  const { t } = useTranslation()
  const { inApp } = usePermissions()
  // Two independent gates, both required:
  //
  //   inApp(kind) — does THIS USER have the app in their RBAC app-scope?
  //   has(kind)   — has the ORG actually subscribed to the app?
  //
  // Only the first was checked, which meant an org admin (whose app-scope
  // covers everything by definition) saw a tray full of apps the org had
  // never bought — clicking one landed on a 403 from require_app. The org
  // gate is the one that decides whether an app exists for this tenant at
  // all, so it belongs here too. The backend still enforces both regardless.
  const { has } = useEntitlements()
  const visibleApps = APPS.filter(app => {
    if (app.hidden) return false
    const kind = APP_KIND_MAP[app.key]
    if (kind === null) return true   // "My" is not a product app — never gated
    return inApp(kind) && has(kind)
  })
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center justify-center w-8 h-8 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
          aria-label={t('appSwitcher.switch', 'Switch app')}
        >
          <LayoutDashboard className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-72 p-3 shadow-xl rounded-2xl border bg-popover"
      >
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1 mb-2">
          {t('appSwitcher.title', 'Applications')}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {visibleApps.map(app => {
            const isCurrent = APP_URLS[app.key] === ''
            const href = isCurrent ? app.href : `${APP_URLS[app.key]}${app.href}`
            const hasLogo = Boolean(app.logo || app.img)

            return (
              <a
                key={app.key}
                style={app.key === 'page' ? { gridRow: 2, gridColumn: 1 } : undefined}
                href={href}
                className={cn(
                  'flex flex-col items-center gap-2 p-3 rounded-xl border transition-all text-center group',
                  isCurrent
                    ? `border-transparent ring-2 ${app.ring} bg-muted/40`
                    : 'border-border hover:border-border/80 hover:bg-muted/30 hover:shadow-sm',
                )}
              >
                {/* Logos render bare (no coloured tile); only the lucide-icon
                    fallback keeps the tinted rounded square. */}
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', !hasLogo && app.color)}>
                  {app.logo
                    ? app.logo
                    : app.img
                    ? (
                      <img
                        src={app.img}
                        alt=""
                        className="w-7 h-7 object-contain"
                        // Each app serves its own copy of the brand marks out of
                        // public/, so an app that hasn't copied one in yet used
                        // to render a broken image in the tray. Hide it and let
                        // the coloured tile stand in rather than show a torn one.
                        onError={event => { event.currentTarget.style.display = 'none' }}
                      />
                    )
                    : <app.icon className="w-5 h-5" />}
                </div>
                <div>
                  <p className="text-[11px] font-bold text-foreground leading-none">{t(`appSwitcher.apps.${app.key}.label`, app.label)}</p>
                  <p className="text-[9px] text-muted-foreground mt-0.5 leading-tight">{t(`appSwitcher.apps.${app.key}.desc`, app.description)}</p>
                </div>
                {isCurrent && (
                  <span className="text-[8px] font-bold uppercase tracking-wider text-primary">
                    {t('appSwitcher.current', 'Current')}
                  </span>
                )}
              </a>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── Notifications ─────────────────────────────────────────────────────────────
// Lives in the top bar (right cluster) alongside the app switcher. Styled to
// match AppSwitcher so the two read as one icon group.
function NotificationBell() {
  const { t, i18n } = useTranslation()
  const { notifications, unreadCount, markAllRead, markRead, clearAll, dismiss } = useNotifications()
  const hasUnread = unreadCount > 0

  return (
    <DropdownMenu onOpenChange={open => { if (!open && hasUnread) markAllRead() }}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('notifications.title', 'Notifications')}
          className="relative flex items-center justify-center w-8 h-8 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
        >
          <Bell className="w-4 h-4" />
          {hasUnread && (
            <span className="absolute -top-0.5 -end-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-primary-foreground ring-2 ring-background">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2.5">
          <DropdownMenuLabel className="p-0 text-sm font-semibold">
            {t('notifications.title', 'Notifications')}
          </DropdownMenuLabel>
          {notifications.length > 0 && (
            <div className="flex items-center gap-1">
              {hasUnread && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <CheckCheck className="h-3 w-3" />
                  {t('notifications.markAllRead', 'Mark all read')}
                </button>
              )}
              <button
                type="button"
                onClick={clearAll}
                aria-label={t('notifications.clearAll', 'Clear all')}
                className="flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />

        {notifications.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <Bell className="mx-auto mb-2 h-5 w-5 text-muted-foreground/50" />
            <p className="text-xs text-muted-foreground">
              {t('notifications.empty', "You're all caught up")}
            </p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto py-1">
            {notifications.map(n => {
              const Icon = n.type === 'order' ? ShoppingBag : AlertTriangle
              return (
                <SwipeToDismiss key={n.id} onDismiss={() => dismiss(n.id)}>
                <DropdownMenuItem
                  onSelect={e => { e.preventDefault(); markRead(n.id) }}
                  className="flex cursor-pointer items-start gap-2.5 px-3 py-2.5 focus:bg-accent"
                >
                  <span className={cn(
                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                    n.type === 'order'
                      ? 'bg-primary/10 text-primary'
                      : 'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
                  )}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={cn(
                        'truncate text-[13px]',
                        n.read ? 'font-medium text-foreground/80' : 'font-semibold text-foreground',
                      )}>
                        {n.title}
                      </p>
                      {!n.read && <span className="ms-auto h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                    </div>
                    {n.body && <p className="truncate text-xs text-muted-foreground">{n.body}</p>}
                    <p className="mt-0.5 text-[10px] text-muted-foreground/70">
                      {formatRelativeTime(n.timestamp, i18n.language)}
                    </p>
                  </div>
                </DropdownMenuItem>
                </SwipeToDismiss>
              )
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── Back ─────────────────────────────────────────────────────────────────────
// The bar always offers a way up. Detail pages used to carry their own
// breadcrumb link and list pages carried nothing, so "back" moved around (or
// vanished) depending on where you stood. One fixed control, always in the
// same place: step through history when there is a history entry to step to,
// otherwise land on the app root.
function BackButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const goBack = () => {
    // React Router stamps an incrementing `idx` on history state. idx > 0 means
    // this session pushed at least one entry, so navigate(-1) stays in the app
    // instead of walking out to whatever page linked here.
    const idx = (typeof window !== 'undefined' && (window.history.state as any)?.idx) ?? 0
    if (idx > 0) navigate(-1)
    else navigate('/')
  }

  return (
    <button
      type="button"
      onClick={goBack}
      aria-label={t('common.back', 'Back')}
      title={t('common.back', 'Back')}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
    >
      <ChevronLeft className="h-[18px] w-[18px]" />
    </button>
  )
}

// ── TopBar ───────────────────────────────────────────────────────────────────
/**
 * Pocket's island (2026-11-03): the latest unread notice, hanging from the
 * middle of the top bar like a phone's dynamic island — a quiet line saying
 * what kind of thing it is and when, then the notice itself beside a small
 * pulsing pixel mark (red when it asks for something to be done). Tapping it
 * opens the page it is about and marks it read; × just marks it read. With
 * several unread it cycles through them. Nothing unread, nothing shown.
 */
const ACTION_EVENTS = new Set(['checklist_overdue', 'stock_expiring'])
// Notices carry paths written for whichever app raised them; these are
// Pocket's own for the same things.
const POS_HREF: Record<string, string> = { '/management/orders': '/orders' }

function PixelMark({ urgent }: { urgent: boolean }) {
  // A plus of five pixels; the centre holds, the arms breathe in turn.
  const cell = (cls: string, delay: number) => (
    <span className={cn('block h-[5px] w-[5px] rounded-[1px] animate-pulse', cls)}
          style={{ animationDelay: `${delay}ms`, animationDuration: '1.4s' }} />
  )
  const tone = urgent ? 'bg-red-400' : 'bg-white/80'
  return (
    <span aria-hidden className="grid shrink-0 grid-cols-3 gap-[1px]" style={{ filter: urgent ? 'drop-shadow(0 0 4px rgb(248 113 113 / .7))' : undefined }}>
      <span />{cell(tone, 0)}<span />
      {cell(tone, 350)}{cell(urgent ? 'bg-red-300' : 'bg-white', 0)}{cell(tone, 700)}
      <span />{cell(tone, 1050)}<span />
    </span>
  )
}

function PosIsland() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { notifications, markRead } = useNotifications()
  // Actions first, then newest first.
  const unread = notifications
    .filter(n => !n.read)
    .sort((a, b) => (Number(ACTION_EVENTS.has(b.event ?? '')) - Number(ACTION_EVENTS.has(a.event ?? '')))
      || b.timestamp.localeCompare(a.timestamp))
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (unread.length < 2) return
    const id = window.setInterval(() => setIndex(i => i + 1), 5000)
    return () => window.clearInterval(id)
  }, [unread.length])
  if (!unread.length) return null
  const n = unread[index % unread.length]
  const urgent = ACTION_EVENTS.has(n.event ?? '')
  const kind = urgent
    ? t('island.actionNeeded', 'Action needed')
    : n.type === 'order' ? t('island.newOrder', 'New order') : t('island.notice', 'Notice')
  const open = () => {
    markRead(n.id)
    if (n.href) navigate(POS_HREF[n.href] ?? n.href)
  }
  return (
    <div className="pointer-events-none absolute inset-x-0 top-full z-50 flex justify-center px-3" data-testid="pos-island">
      <div key={n.id}
           className="pointer-events-auto flex max-w-[min(92vw,26rem)] items-center gap-1 rounded-b-[22px] bg-black ps-5 pe-2 pb-2.5 pt-2 text-white shadow-xl animate-in fade-in-0 slide-in-from-top-2 duration-300">
        <button type="button" onClick={open} className="min-w-0 flex-1 text-start">
          <span className="block truncate text-[12px] text-white/55">
            {kind} · {formatRelativeTime(n.timestamp, i18n.language)}
            {unread.length > 1 && <span className="ms-1.5 rounded-full bg-white/15 px-1.5 text-[10px] font-semibold text-white/80">+{unread.length - 1}</span>}
          </span>
          <span className="mt-0.5 flex items-center gap-2.5">
            <PixelMark urgent={urgent} />
            <span className="truncate text-[15px] font-medium">{n.title}{n.body ? <span className="text-white/60"> · {n.body}</span> : null}</span>
          </span>
        </button>
        <button type="button" onClick={() => markRead(n.id)} aria-label={t('island.dismiss', 'Dismiss')}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-white/50 hover:bg-white/10 hover:text-white">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

/**
 * Pocket back office's page title (2026-11-02): in the top bar, greyish, so it
 * names the page without competing with it. What the page is for — the line
 * that used to sit under each page's own heading — is its tooltip, there when
 * asked for rather than read on every visit. Tap shows it too, on a phone.
 */
function PosPageTitle({ title, about }: { title: string; about?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <h1 tabIndex={0} onClick={() => setOpen(o => !o)} data-testid="topbar-page-title"
              className="min-w-0 cursor-default truncate text-[15px] font-semibold text-muted-foreground outline-none">
            {title}
          </h1>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-xs text-xs leading-snug">
          {about || title}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** Phone-only overflow for the top bar's right cluster: everything except the
 *  bell moves into a bottom sheet, so the page title keeps the room. */
function PhoneOverflow({ children }: { children?: ReactNode }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('common.more', 'More')}
        className="flex h-10 w-10 items-center justify-center rounded-md text-foreground/70 active:bg-muted"
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[max(1rem,env(safe-area-inset-bottom))]">
          <SheetHeader>
            <SheetTitle>{t('common.more', 'More')}</SheetTitle>
          </SheetHeader>
          {/* The same controls as the desktop bar, given finger-sized room —
              and each one's (translated) aria-label printed under its icon,
              since a row of bare icons says nothing on a phone. */}
          <div className="mt-4 flex flex-wrap items-start gap-2 [&_button]:!h-auto [&_button]:!w-20 [&_button]:flex-col [&_button]:gap-1.5 [&_button]:rounded-xl [&_button]:py-3 [&_button]:after:content-[attr(aria-label)] [&_button]:after:text-[11px] [&_button]:after:leading-tight [&_button]:after:text-center">
            {children}
            <TeamButton />
            <AppSwitcher />
          </div>
          <div className="mt-4">
            <VersionBadge />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

/** "Sun 2 Nov · 19:42" — this device's local time, ticking each minute. */
function TopBarClock({ compact = false }: { compact?: boolean }) {
  const { i18n } = useTranslation()
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    // Line up with the minute, then tick once a minute.
    let interval: number | undefined
    const first = window.setTimeout(() => {
      setNow(new Date())
      interval = window.setInterval(() => setNow(new Date()), 60_000)
    }, 60_000 - (Date.now() % 60_000))
    return () => { window.clearTimeout(first); if (interval) window.clearInterval(interval) }
  }, [])
  const locale = i18n.language || undefined
  const day = now.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })
  const time = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  return (
    <time
      dateTime={now.toISOString()}
      data-testid="topbar-clock"
      className={compact
        ? 'inline-flex items-baseline gap-1.5'
        : 'hidden items-baseline gap-1.5 rounded-md border border-border px-2.5 py-1 sm:inline-flex'}
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{day}</span>
      <span className="font-mono text-sm font-semibold tabular-nums text-foreground">{time}</span>
    </time>
  )
}

export default function TopBar(
  { children, compactOnPhone = false }: {
    children?: ReactNode
    /** Phone only: keep the bell, fold the other right-hand controls into a
     *  "More" sheet, and drop the drawer trigger (the app's bottom tab bar
     *  carries "More"). Opt-in, so apps without a tab bar are unchanged. */
    compactOnPhone?: boolean
  },
) {
  const { title, subtitle, render } = usePageHeaderValue()
  const atRoot = useLocation().pathname === '/'
  const compact = useIsMobile() && compactOnPhone

  // Pages can take over the bar entirely (e.g. the playlist info bar) by
  // passing a `render` node via usePageHeader. When present we skip the
  // default sidebar-trigger / title / actions / app-switcher layout and
  // give the page the full h-14 strip to lay out as it likes.
  if (render) {
    return (
      <div className="brand-chrome-light h-14 border-b border-border flex items-center shrink-0 z-40 relative">
        {render}
      </div>
    )
  }

  return (
    <div className="brand-chrome-light h-14 border-b border-border flex items-center justify-between gap-3 px-4 sm:px-fluid-l shrink-0 z-40 relative">
      {IS_POS_APP && <PosIsland />}
      {/* Left: mobile trigger + current page title */}
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        {!compact && <SidebarTrigger className="md:hidden w-8 h-8 text-foreground/70 hover:text-foreground hover:bg-muted rounded-md" />}
        {/* Not at the root. "Back" exists to go UP, and the dashboard is the
            top — the control there either did nothing visible or walked the
            operator out of the app entirely, into whatever page linked here. */}
        {/* Pocket's back office has no back button: the sidebar (the dock on
            a phone) is the way between pages. Its title is here, quiet. */}
        {!atRoot && !IS_POS_APP && <BackButton />}
        {IS_POS_APP && title && <PosPageTitle title={title} about={subtitle} />}
        {!IS_POS_APP && (
          <div className="min-w-0 flex flex-col">
            <h1 className="text-[15px] font-semibold text-foreground leading-tight truncate">
              {title ?? ''}
            </h1>
            {subtitle && (
              <p className="hidden sm:block text-[11px] text-muted-foreground leading-tight truncate">{subtitle}</p>
            )}
          </div>
        )}
      </div>

      {/* Right: notifications + app switcher. Per-page actions are NOT
          here — see the note on PageHeaderValue. */}
      {compact ? (
        <div className="flex items-center gap-1 shrink-0">
          {IS_POS_APP && <TopBarClock compact />}
          <NotificationBell />
          <PhoneOverflow>{children}</PhoneOverflow>
        </div>
      ) : (
      <div className="flex items-center gap-2 shrink-0">
        {/* The time, on Pocket's back office: shifts, due times and
            deliveries all run on the clock, so it is always in view. */}
        {IS_POS_APP && <TopBarClock />}
        {/* Staging build stamp (version · build time). Renders nothing in prod.
            Pocket's back office shows it in the bottom corner instead. */}
        {!IS_POS_APP && <VersionBadge />}
        {/* No Install button here. Pocket is TWO installable apps — the
            register and the kitchen display — and one button in shared chrome
            can only ever offer the register, on every page, next to the pair
            of tiles the dashboard already carries. Installing lives on the
            dashboard (apps/pos InstallApps) and on /kds, which is the only
            page where the kitchen manifest is live. */}
        {children}
        {/* Quick access to the team from any app (hidden in My, which has the
            full Users page, and from anyone who cannot see the team). */}
        <TeamButton />
        <NotificationBell />
        <AppSwitcher />
      </div>
      )}
    </div>
  )
}

// ── Swipe to dismiss ─────────────────────────────────────────────────────────
/**
 * A row that can be swiped away sideways on a touch screen.
 *
 * Tracks a horizontal drag with pointer events, follows the finger, and past
 * a third of the row's width lets go — the row slides out and `onDismiss`
 * fires. A short or mostly-vertical drag snaps back and is not a tap, so a
 * scroll through the list never dismisses anything. Mouse users keep the
 * "Clear all" button; nothing here intercepts a click.
 */
function SwipeToDismiss({ children, onDismiss }: { children: ReactNode; onDismiss: () => void }) {
  const [dx, setDx] = useState(0)
  const [leaving, setLeaving] = useState<null | 1 | -1>(null)
  const start = useRef<{ x: number; y: number; id: number } | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!start.current || start.current.id !== e.pointerId) return
    const dxNow = e.clientX - start.current.x
    const dyNow = e.clientY - start.current.y
    // Mostly vertical = a scroll. Let it be one.
    if (Math.abs(dyNow) > Math.abs(dxNow) && Math.abs(dxNow) < 8) return
    if (Math.abs(dxNow) > 8) ref.current?.setPointerCapture(e.pointerId)
    setDx(dxNow)
  }
  const onPointerUp = () => {
    if (!start.current) return
    start.current = null
    const width = ref.current?.offsetWidth ?? 320
    if (Math.abs(dx) > width / 3) {
      setLeaving(dx > 0 ? 1 : -1)
      setTimeout(onDismiss, 160)
    } else {
      setDx(0)
    }
  }

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { start.current = null; setDx(0) }}
      style={{
        transform: leaving ? `translateX(${leaving * 120}%)` : `translateX(${dx}px)`,
        opacity: leaving ? 0 : 1 - Math.min(Math.abs(dx) / 320, 0.6),
        transition: start.current ? 'none' : 'transform 160ms ease-out, opacity 160ms ease-out',
        touchAction: 'pan-y',
      }}
    >
      {children}
    </div>
  )
}
